import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useDispatch } from "react-redux";
import { clearCart } from "../../redux/slices/cart";
import "./PaymentForm.css";

// Payment form component that uses Stripe hooks
const PaymentForm = ({ orderData, onBack, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [paymentError, setPaymentError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setPaymentError("Payment system is not ready. Please try again.");
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setPaymentError("Card information is missing.");
      setIsProcessing(false);
      return;
    }

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${orderData.billing.first_name} ${orderData.billing.last_name}`,
              email: orderData.billing.email,
              phone: orderData.billing.phone,
              address: {
                line1: orderData.billing.address_1,
                city: orderData.billing.city,
                state: orderData.billing.state,
                postal_code: orderData.billing.postcode,
                country: orderData.billing.country || "US",
              },
            },
          },
        }
      );

      if (error) {
        console.error("Payment failed:", error);
        setPaymentError(error.message || "Payment failed. Please try again.");
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        console.log("Payment succeeded:", paymentIntent);
        setPaymentSucceeded(true);

        dispatch(clearCart());
      } else {
        setPaymentError("Payment was not completed. Please try again.");
      }
    } catch (err) {
      console.error("Error confirming payment:", err);
      setPaymentError("An unexpected error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSucceeded) {
    return (
      <section className="PaymentStep">
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <h2 style={{ color: "#4caf50", marginBottom: "20px" }}>
            🎉 Payment Successful!
          </h2>
          <p>Thank you for your order #{orderData.orderId}!</p>
          <p>You will receive a confirmation email shortly.</p>
          <button
            onClick={() => (window.location.href = "/")}
            style={{
              marginTop: "20px",
              backgroundColor: "#007cba",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Continue Shopping
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="PaymentStep">
      <p>PAYMENT DETAILS</p>

      <div className="billing-summary">
        <p>Billing Address:</p>
        <p>
          {orderData.billing.first_name} {orderData.billing.last_name}
        </p>
        <p>{orderData.billing.address_1}</p>
        <p>
          {orderData.billing.city}, {orderData.billing.state}{" "}
          {orderData.billing.postcode}
        </p>
        <p>{orderData.billing.email}</p>
        <p style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Order #{orderData.orderId}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card-element">
          <p>Card Details:</p>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  letterSpacing: "0.025em",
                  fontFamily: "Source Code Pro, monospace",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
              hidePostalCode: true, // Since we already have postal code in billing
            }}
          />
        </div>

        {paymentError && (
          <div
            className="error"
            style={{
              color: "red",
              margin: "10px 0",
              padding: "10px",
              backgroundColor: "#ffeaea",
              border: "1px solid #ffcdd2",
              borderRadius: "4px",
            }}
          >
            {paymentError}
          </div>
        )}

        <div style={{ marginTop: "20px" }}>
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            style={{
              marginRight: "10px",
              backgroundColor: "#f5f5f5",
              color: "#333",
              padding: "10px 20px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: isProcessing ? "not-allowed" : "pointer",
            }}
          >
            Back to Billing
          </button>
          <button
            type="submit"
            disabled={isProcessing || !stripe}
            style={{
              backgroundColor: isProcessing ? "#ccc" : "#007cba",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "4px",
              cursor: isProcessing ? "not-allowed" : "pointer",
              minWidth: "150px",
            }}
          >
            {isProcessing ? "Processing..." : "Complete Payment"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default PaymentForm;
